import { html } from 'lit';
import { ui } from '../bidi-html';

/**
 * A card hanging as a sign over the town (the round, tending, the first lesson): its surface carries `sign`, and
 * the view hosting it draws the thumb zone's top row (progress, Skip) and foot (Next or Show me, notes). A spot it
 * card lays its own beat between the two, so the zone is one fixed block at the screen's foot. Every card but a
 * flashcard splits itself the same way: its stem in the cream bubble, its question and answers in the zone.
 */
export interface SignHost {
  signParts(): { top: unknown; foot: unknown };
  /** The card is heard only (a by-ear choice). */
  heardOnly?(): boolean;
}

/** The `<a2ui-surface>` a card is drawn in, or null when it is not in one. */
export function surfaceOf(el: Element): Element | null {
  let node: Node = el;
  for (;;) {
    const root = node.getRootNode();
    if (!(root instanceof ShadowRoot)) return null;
    const host = root.host;
    if (host.localName === 'a2ui-surface') return host;
    node = host;
  }
}

/** The view a card hangs from, or null when the card covers the screen as usual. */
export function signHostOf(el: Element): SignHost | null {
  const host = surfaceOf(el);
  if (!host?.hasAttribute('sign')) return null;
  const view = host.closest('review-view') as (Element & Partial<SignHost>) | null;
  return view && typeof view.signParts === 'function' ? (view as unknown as SignHost) : null;
}

/** The thumb zone a hanging card draws in its own surface: the view's top row, the card's beat, the view's foot. */
export function signZone(host: SignHost, beat: unknown) {
  const { top, foot } = host.signParts();
  return html`<div class="sign-zone" dir=${ui()}>${top}<div class="sign-beat">${beat}</div>${foot}</div>`;
}
